import React from "react";
import Loading from "./components/atoms/Loading";
import PaymentConfirm from "./components/templates/PaymentConfirm";
import PaymentForm from "./components/templates/PaymentForm";
import { useAlert } from "react-alert";
import PaymentSuccess from "./components/templates/PaymentSuccess";

// ネイティブ側で用意しているメソッド呼び出しのハンドラー
interface NativeHandler {
  postMessage(message: string): void;
}
declare var PayScreen: NativeHandler;

const params = {
  headers: { "Content-Type": "application/json; charset=utf-8" },
};

const PAYUMENT_KBN = "1";
const PAYMENT_ITEM_CD = "56";
const APPLICATION_KBN = "126";
const TDSECURE_USING_FLG = "1";
const PHRASE_BRANCH_MASTER_UNAVAILABLE = "支店マスタが取得できませんでした。";
const ERROR_MSG_INVALID_CARD =
  "入力されたカード情報に誤りがあります。再度、入力をお願いします。";
const ERROR_MSG_BRANCH_MASTER_NOT_FOUND =
  "自治会等の口座が登録されていません。役員の方へ、口座登録をご依頼ください。";
const ERROR_MSG_PAYMENT_FAILED =
  "決済処理が失敗しました。初めから支払い処理をお願いします。";

/**
 * 二重URLエンコードされたShift_JIS文字列をデコードする関数
 * @param input 二重URLエンコードされた文字列
 * @returns デコード後の日本語文字列
 */
const decodeUrlShiftJIS = (input: string) => {
  // %xx をバイト列に変換
  const bytes: number[] = [];
  for (let i = 0; i < input.length; ) {
    if (input[i] === "%" && i + 2 < input.length) {
      const hex = input.slice(i + 1, i + 3);
      bytes.push(parseInt(hex, 16));
      i += 3;
    } else {
      // 非%部分は latin-1 としてバイト化
      bytes.push(input.charCodeAt(i) & 0xff);
      i += 1;
    }
  }

  // Shift_JISでデコード（ブラウザのTextDecoderを使用）
  const decoder = new TextDecoder("shift_jis", { fatal: false });
  return decoder.decode(new Uint8Array(bytes));
};

function App() {
  const alert = useAlert();
  const [state, setState] = React.useState({
    ccYear: "",
    ccMonth: "",
    securityCode: "",
    ccNumber: "",
    billingFirstName: "",
    billingLastName: "",
    //above is value form info payment
    step: 1,
    //above is step payment
    amount: 0,
    billingId: 0,
    to: "",
    payee: "",
    payeeEmail: "",
    merchantId: "",
    serviceId: "",
    title: "",
    token: "",
    tokenKey: "",
    tds2infoToken: "",
    tds2infoTokenKey: "",
    tokenError: "",
    tds2infoTokenError: "",
    maskedCcNumber: "",
    officeCd: "",
    payeeAccountId: 0,
    membershipCd: "",
    contractNoPrefix: "",
  });
  // PaymentConfirmでも連打防止のため、stateによる制御がされているがstateだけでは甘いため、Refも使用する
  const isSubmittingRef = React.useRef(false);
  const isPayingRef = React.useRef(false);
  const isShowingAlertRef = React.useRef(false);
  const firstRef = React.useRef(false);
  const url = React.useMemo(() => new URL(window.location.href), []);
  const searchParams = React.useMemo(() => new URLSearchParams(url.search), [
    url,
  ]);
  const tokenCode = url.pathname.split("/").filter(Boolean).slice(-1)[0];
  const isTypePc = searchParams.get("type") === "pc";
  const paymentState = searchParams.get("payment_state");
  const paymentErrorMessage = React.useMemo(() => {
    const message = searchParams.get("errorMsg");
    if (!message) return ERROR_MSG_INVALID_CARD;
    const decodeMessage = decodeUrlShiftJIS(message);
    return decodeMessage.startsWith(PHRASE_BRANCH_MASTER_UNAVAILABLE)
      ? ERROR_MSG_BRANCH_MASTER_NOT_FOUND
      : ERROR_MSG_PAYMENT_FAILED;
  }, [searchParams]);
  const paymentSuccessUrl = React.useMemo(() => {
    const paymentUrl = new URL(url);
    const params = isTypePc
      ? new URLSearchParams({
          type: "pc",
          payment_state: "success",
        })
      : new URLSearchParams({
          payment_state: "success",
        });
    paymentUrl.search = params.toString();
    return paymentUrl;
  }, [isTypePc, url]);
  const paymentErrorUrl = React.useMemo(() => {
    const paymentUrl = new URL(url);
    const params = isTypePc
      ? new URLSearchParams({
          type: "pc",
          payment_state: "error",
        })
      : new URLSearchParams({
          payment_state: "error",
        });
    paymentUrl.search = params.toString();
    return paymentUrl;
  }, [isTypePc, url]);

  const showAlertOnce = React.useCallback(
    (message: React.ReactNode, onClose?: () => void) => {
      if (isShowingAlertRef.current) return;
      isShowingAlertRef.current = true;
      alert.show(message, {
        onClose: () => {
          onClose?.();
          isShowingAlertRef.current = false;
        },
      });
    },
    [alert]
  );

  const freeCsv = React.useMemo(
    () =>
      [
        `DEALINGS_TYPE=10`,
        `TOKEN=${state.token}`,
        `TOKEN_KEY=${state.tokenKey}`,
        `TDS2INFO_TOKEN=${state.tds2infoToken}`,
        `TDS2INFO_TOKEN_KEY=${state.tds2infoTokenKey}`,
      ].join(","),
    [state.token, state.tokenKey, state.tds2infoToken, state.tds2infoTokenKey]
  );

  const redirectBillDetail = React.useCallback(() => {
    if (!isTypePc) {
      // 決済情報を取得し集金詳細の画面に遷移（スマホの場合）
      PayScreen.postMessage("moveToCompletedPage");
    }
  }, [isTypePc]);

  // 決済成功時My自治会のbilling更新
  const handlePayment = React.useCallback(async () => {
    if (!tokenCode || !state.billingId) return;
    if (isPayingRef.current) return;
    isPayingRef.current = true;
    try {
      let url = `${
        process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : ""
      }/api/v1/payments/${tokenCode}`;
      const data = await fetch(url, {
        ...params,
        method: "put",
        body: JSON.stringify({
          billingId: state.billingId,
        }),
      });
      if (data.ok) {
        setState((prevData) => ({
          ...prevData,
          step: 3,
        }));
      } else {
        const res = await data?.clone().json();
        showAlertOnce(res?.message || "決済に失敗しました", () =>
          setState((s) => ({
            ...s,
            token: "",
            tokenKey: "",
            tds2infoToken: "",
            tds2infoTokenKey: "",
          }))
        );
      }
    } catch (error) {
      console.log(error);
      showAlertOnce("ネットワークエラー", () =>
        setState((s) => ({
          ...s,
          token: "",
          tokenKey: "",
          tds2infoToken: "",
          tds2infoTokenKey: "",
        }))
      );
    } finally {
      isPayingRef.current = true;
    }
  }, [showAlertOnce, state.billingId, tokenCode]);

  // 都度決済リンクの成功確認
  const handlePaymentState = React.useCallback(() => {
    if (!paymentState) return;
    if (!isTypePc) {
      // スマホの戻る対策
      if (!firstRef.current) {
        firstRef.current = true;
        // ブラウザバックを検知
        window.addEventListener("popstate", redirectBillDetail);

        // ダミーの履歴を追加して都度決済リンクの履歴に飛ばないように
        window.history.replaceState({ completed: true }, "", url.href);
        const DUMMY_COUNT = 2; // 連続バック対策
        for (let i = 0; i < DUMMY_COUNT; i++) {
          window.history.pushState(
            { trap: true, idx: i },
            "",
            url.href + "#done" + i
          );
        }
      }
    }
    switch (paymentState) {
      // payment_state=success である場合
      case "success":
        handlePayment();
        break;

      // payment_state=error である場合
      case "error":
        showAlertOnce(paymentErrorMessage, () =>
          setState((s) => ({
            ...s,
            token: "",
            tokenKey: "",
            tds2infoToken: "",
            tds2infoTokenKey: "",
          }))
        );
        break;

      default:
        // payment_state パラメーターはあるが、上記以外の値である場合
        showAlertOnce(`予期しない payment_state: ${paymentState} です`, () =>
          setState((s) => ({
            ...s,
            token: "",
            tokenKey: "",
            tds2infoToken: "",
            tds2infoTokenKey: "",
          }))
        );
    }
    isSubmittingRef.current = false; // 決済処理連打防対応
  }, [
    handlePayment,
    isTypePc,
    paymentErrorMessage,
    paymentState,
    redirectBillDetail,
    showAlertOnce,
    url.href,
  ]);

  React.useEffect(() => {
    (async () => {
      try {
        let url = `${
          process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : ""
        }/api/v1/payments/${tokenCode}`;
        const data = await fetch(url, { ...params, method: "get" });
        if (data.ok) {
          const res = await data?.clone().json();
          setState((s) => ({
            ...s,
            ...res,
          }));
        } else {
          const res = await data?.clone().json();
          if (res?.message) {
            showAlertOnce(res.message, () => {
              if (isTypePc) {
                return window.location.replace(
                  `${process.env.REACT_APP_WEB_URL ?? ""}bills`
                );
              } else {
                // 決済情報を取得し集金詳細の画面に遷移（スマホの場合）
                PayScreen.postMessage("moveToCompletedPage");
              }
            });
          } else {
            showAlertOnce("有効期限が切れています");
          }
        }
      } catch (error) {
        showAlertOnce("ネットワークエラー");
      }
    })();
  }, [alert, isTypePc, showAlertOnce, tokenCode]);

  React.useEffect(() => {
    handlePaymentState();
  }, [handlePaymentState]);

  const afterGenerateTds2Token = React.useCallback(
    (response: GenerateTds2TokenResponse) => {
      if (response.result === "OK") {
        const tokenResponse = response.tokenResponse;

        setState((s) => ({
          ...s,
          tds2infoToken: tokenResponse.tds2infoToken,
          tds2infoTokenKey: tokenResponse.tds2infoTokenKey,
        }));
      } else {
        setState((s) => ({
          ...s,
          tds2infoTokenError: response.errorCode,
          token: "",
          tokenKey: "",
        }));
      }
    },
    []
  );

  const afterGenerateToken = React.useCallback(
    (comSbsSystemTds2Request: {
      billingFirstName: string;
      billingLastName: string;
    }) => (response: GenerateTokenResponse) => {
      if (response.result === "OK") {
        const tokenResponse = response.tokenResponse;
        setState((s) => ({
          ...s,
          token: tokenResponse.token,
          tokenKey: tokenResponse.tokenKey,
          maskedCcNumber: tokenResponse.maskedCcNumber,
        }));
        com_sbps_system_tds2.generateToken(
          {
            ...comSbsSystemTds2Request,
            merchantId: state.merchantId,
            serviceId: state.serviceId,
            email: state.payeeEmail,
          },
          afterGenerateTds2Token
        );
      } else {
        setState((s) => ({ ...s, tokenError: response.errorCode }));
      }
    },
    [
      afterGenerateTds2Token,
      state.payeeEmail,
      state.merchantId,
      state.serviceId,
    ]
  );

  const redirectExternalPayment = React.useCallback(() => {
    const baseUrl = process.env.REACT_APP_EXTERNAL_PAYMENT_API_URL;

    const params = new URLSearchParams({
      membershipCd: state.membershipCd,
      officeCd: state.officeCd, // 支店コード 決済情報取得に追加
      paymentKbn: PAYUMENT_KBN, // 決済区分
      paymentItemCd: PAYMENT_ITEM_CD, // 決済品目
      contractNo: state.contractNoPrefix + state.billingId.toString(36), // 管理番号(これでハウスペイメントとmy自治会の決済を照合する)
      paymentUserId: state.payeeAccountId.toString(36), //ユーザーID
      tenantName: state.payee, // 入居者名
      propertyName: state.title, // 物件名
      amount: state.amount.toString(), // 決済金額
      successUrl: paymentSuccessUrl.toString(), // 完了時URL
      cancelUrl: paymentErrorUrl.toString(), // ハウスペイメントの画面を利用しないため、これは使用されないが、必須のため入れておく
      errorUrl: paymentErrorUrl.toString(), // エラー時URL
      applicationKbn: APPLICATION_KBN, // 申込区分
      tdsecureUsingFlg: TDSECURE_USING_FLG, // 1
      freeCsv: freeCsv, //ワンタイムトークン/トークンキー 利用者情報トークントークンキー DEALINGSTYPE=10
    });

    const redirectUrl = `${baseUrl}?${params.toString()}`;
    console.log("redirectUrl: ", redirectUrl);

    // 画面遷移を実行
    window.location.replace(redirectUrl);
  }, [
    freeCsv,
    paymentErrorUrl,
    paymentSuccessUrl,
    state.amount,
    state.billingId,
    state.contractNoPrefix,
    state.membershipCd,
    state.officeCd,
    state.payee,
    state.payeeAccountId,
    state.title,
  ]);

  const confirmFunc = React.useCallback(
    (args: {
      ccNumber: string;
      ccExpiration: string;
      securityCode: string;
      billingFirstName: string;
      billingLastName: string;
    }) => {
      setState((s) => ({ ...s, tokenError: "", tds2infoTokenError: "" }));
      const comSbsSystemRequest = {
        ccNumber: args.ccNumber,
        ccExpiration: args.ccExpiration,
        securityCode: args.securityCode,
      };
      const comSbsSystemTds2Request = {
        billingFirstName: args.billingFirstName,
        billingLastName: args.billingLastName,
      };
      com_sbps_system.generateToken(
        {
          ...comSbsSystemRequest,
          merchantId: state.merchantId,
          serviceId: state.serviceId,
        },
        afterGenerateToken(comSbsSystemTds2Request)
      );
    },
    [state.merchantId, state.serviceId, afterGenerateToken]
  );

  const submitFunc = React.useCallback(async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    redirectExternalPayment();
  }, [redirectExternalPayment]);

  const handleBackBillDetail = (action: string) => {
    if (isTypePc) {
      return window.location.replace(
        `${
          process.env.REACT_APP_WEB_URL ? process.env.REACT_APP_WEB_URL : ""
        }bills/${state.billingId}`
      );
    } else {
      PayScreen.postMessage(action);
    }
  };

  const handleBackFormPayment = () => {
    setState((s) => ({
      ...s,
      token: "",
      tokenKey: "",
      tds2infoToken: "",
      tds2infoTokenKey: "",
    }));
  };

  if (state.step === 3) {
    return (
      <PaymentSuccess
        isTypePc={isTypePc}
        onBackBillDetail={handleBackBillDetail}
      />
    );
  }

  return state.to ? (
    state.tokenKey && state.tds2infoTokenKey ? (
      <PaymentConfirm
        isTypePc={isTypePc}
        to={state.to}
        amount={state.amount}
        payee={state.payee}
        title={state.title}
        maskedCcNumber={state.maskedCcNumber}
        billingName={`${state.billingFirstName} ${state.billingLastName}`}
        onBackFormPayment={handleBackFormPayment}
        submit={submitFunc}
      />
    ) : (
      <PaymentForm
        isTypePc={isTypePc}
        handleBackBillDetail={handleBackBillDetail}
        tokenError={state.tokenError}
        tds2infoTokenError={state.tds2infoTokenError}
        title={state.title}
        ccYear={state.ccYear}
        ccMonth={state.ccMonth}
        securityCode={state.securityCode}
        ccNumber={state.ccNumber}
        billingFirstName={state.billingFirstName}
        billingLastName={state.billingLastName}
        setValueForm={setState}
        submit={confirmFunc}
      />
    )
  ) : (
    <Loading />
  );
}
export default App;
